import type { ReactNode } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { usePhone } from "@/shared/lib/media";
import { Empty, ErrorBox, isEmpty } from "./Page";

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  align?: "start" | "end" | "center";
  className?: string;
  hideOnMobile?: boolean;
  /** Left out entirely, e.g. a warehouse column while there is one warehouse. */
  hidden?: boolean;
  /** What a row is called, leading its card on a phone (a customer's name rather than the invoice's number). */
  primary?: boolean;
  /** Only on a phone's card, never in the table (one line saying what two columns say, "12 × 15,600.00"). */
  phoneOnly?: boolean;
}

/**
 * How a row reads as a card on a phone: the primary column (else the first) leads, the
 * last amount shown on phones ends it, and the other columns follow as one line, an amount
 * there under its heading (`labelled`) so a row of figures says which is which. A column
 * without a heading (a row's buttons) stays out.
 */
export function cardParts<T>(columns: Column<T>[]): { title: Column<T>; trailing?: Column<T>; rest: Column<T>[]; labelled: string[] } {
  const shown = columns.filter((c) => !c.hidden);
  const title = shown.find((c) => c.primary) ?? shown[0];
  const headed = (c: Column<T>) => c.header !== "" && c.header != null;
  const trailing = [...shown].reverse().find((c) => c !== title && c.align === "end" && !c.hideOnMobile && headed(c));
  const rest = shown.filter((c) => c !== title && c !== trailing && !c.hideOnMobile && headed(c));
  return { title, trailing, rest, labelled: rest.filter((c) => c.align === "end").map((c) => c.key) };
}

export function DataTable<T extends object>(props: {
  columns: Column<T>[];
  rows: T[];
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  /** What to do first, under the empty message (a button). */
  emptyAction?: ReactNode;
  /** Shown instead of the empty message when nothing has been added yet (rather than
   *  nothing matching a search or filter): a FirstUse. */
  blank?: ReactNode;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  footer?: ReactNode;
  rowKey?: (row: T) => string;
  /** Rows chosen to act on together: a checkbox starts each row, and one in the header takes them all. */
  selection?: { ids: string[]; onChange: (ids: string[]) => void };
  /** On a phone: rows as cards (the default for lists whose rows open something) or the table itself. */
  phone?: "cards" | "table";
}) {
  const { t } = useTranslation();
  const phone = usePhone();
  if (props.error) return <div className="py-4"><ErrorBox error={props.error} onRetry={props.onRetry} /></div>;
  if (!props.loading && props.rows.length === 0) return props.blank ?? <Empty title={props.empty ?? t("common.empty")} action={props.emptyAction} />;
  const align = (a?: string) => (a === "end" ? "text-end" : a === "center" ? "text-center" : "text-start");
  const keyOf = (row: T, i: number) => props.rowKey?.(row) ?? (row as { id?: string }).id ?? String(i);
  // One or the other is on the page, never both: a hidden copy would answer searches for its text.
  const cards = phone && (props.phone ?? (props.onRowClick ? "cards" : "table")) === "cards";
  const sel = props.selection;
  const allIds = props.rows.map(keyOf);
  const all = !!sel && allIds.length > 0 && allIds.every((id) => sel.ids.includes(id));
  const pick: Column<T>[] = sel ? [{
    key: "__pick", className: "w-8",
    header: <input type="checkbox" className="size-4 accent-ledger align-middle" aria-label={t("common.pickAll")} checked={all}
      ref={(el) => { if (el) el.indeterminate = !all && sel.ids.length > 0; }}
      onChange={() => sel.onChange(all ? [] : allIds)} />,
    cell: (row) => {
      const id = keyOf(row, props.rows.indexOf(row));
      const on = sel.ids.includes(id);
      return <input type="checkbox" className="size-4 accent-ledger align-middle" aria-label={t("common.pickRow")} checked={on}
        onClick={(e) => e.stopPropagation()} onChange={() => sel.onChange(on ? sel.ids.filter((x) => x !== id) : [...sel.ids, id])} />;
    },
  }] : [];
  const columns = [...pick, ...props.columns.filter((c) => !c.hidden && !c.phoneOnly)];
  const cell = (first: boolean, last: boolean) => clsx("px-3", first && "ps-0", last && "pe-0");
  const head = (
    <thead>
      <tr className="border-b border-line text-[12.5px] text-ink-3">
        {columns.map((c, i) => (
          <th key={c.key} scope="col" className={clsx("py-2.5 font-medium whitespace-nowrap", cell(i === 0, i === columns.length - 1), align(c.align), c.hideOnMobile && "max-md:hidden", c.className)}>
            {c.header}
          </th>
        ))}
      </tr>
    </thead>
  );
  const table = (body: ReactNode) => (
    <div className="scroll-shadow overflow-x-auto" aria-busy={props.loading || undefined}>
      <table className="w-full border-collapse">
        {head}
        {body}
        {!props.loading && props.footer && <tfoot>{props.footer}</tfoot>}
      </table>
    </div>
  );
  const { title, trailing, rest, labelled } = cardParts(props.columns);

  // While loading, the list keeps its shape: the headers, and placeholder rows where the rows will be
  // (hidden from screen readers and from anything looking for the rows, which arrive after).
  if (props.loading) {
    return (
      <>
        {cards ? (
          <ul className="divide-y divide-line-2" aria-busy="true">
            {[0, 1, 2, 3, 4].map((r) => (
              <li key={r} aria-hidden="true" className="flex items-center gap-3 py-3.5">
                <span className="flex-1 space-y-2"><span className="block h-3 w-1/2 animate-pulse rounded bg-line-2" /><span className="block h-2.5 w-3/4 animate-pulse rounded bg-line-2" /></span>
                <span className="h-3 w-16 animate-pulse rounded bg-line-2" />
              </li>
            ))}
          </ul>
        ) : table(
          <tbody>
            {[0, 1, 2, 3, 4].map((r) => (
              <tr key={r} aria-hidden="true" className="border-b border-line-2 last:border-0">
                {columns.map((c, i) => (
                  <td key={c.key} className={clsx("py-4", cell(i === 0, i === columns.length - 1), c.hideOnMobile && "max-md:hidden")}>
                    <span className={clsx("block h-3 animate-pulse rounded bg-line-2", c.align === "end" && "ms-auto", ["w-3/4", "w-1/2", "w-2/3"][(r + i) % 3])} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>,
        )}
      </>
    );
  }
  return (
    <>
      {cards ? (
        <ul className="divide-y divide-line-2">
          {props.rows.map((row, i) => {
            const line = rest.map((c) => ({ key: c.key, v: c.cell(row), label: labelled.includes(c.key) ? c.header : null })).filter((x) => !isEmpty(x.v));
            return (
              <li key={keyOf(row, i)} {...(props.onRowClick && {
                role: "link", tabIndex: 0, onClick: () => props.onRowClick!(row), onKeyDown: (e: React.KeyboardEvent) => e.key === "Enter" && props.onRowClick!(row),
              })} className={clsx("flex items-center gap-3 py-3", props.onRowClick && "cursor-pointer active:bg-paper")}>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold">{title.cell(row)}</div>
                  {line.length > 0 && (
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[13px] text-ink-2">
                      {line.map((x, j) => <span key={x.key} className="inline-flex min-w-0 items-center gap-2">{j > 0 && <span className="text-ink-3" aria-hidden>·</span>}{x.label && <span className="text-ink-3">{x.label}</span>}{x.v}</span>)}
                    </div>
                  )}
                </div>
                {trailing && <div className="shrink-0 text-end">{trailing.cell(row)}</div>}
              </li>
            );
          })}
        </ul>
      ) : table(
        <tbody>
          {props.rows.map((row, i) => (
            <tr
              key={keyOf(row, i)}
              onClick={props.onRowClick ? () => props.onRowClick!(row) : undefined}
              onKeyDown={props.onRowClick ? (e) => e.key === "Enter" && props.onRowClick!(row) : undefined}
              tabIndex={props.onRowClick ? 0 : undefined}
              className={clsx("border-b border-line-2 last:border-0", props.onRowClick && "cursor-pointer hover:bg-paper/70 focus-visible:bg-paper")}
            >
              {columns.map((c, j) => {
                const content = c.cell(row);
                // Dates, numbers, codes and names stay on one line; only long text wraps.
                const short = (typeof content === "string" && content.length <= 30) || typeof content === "number";
                return (
                  <td key={c.key} className={clsx("py-3 align-middle", cell(j === 0, j === columns.length - 1), short && "whitespace-nowrap", align(c.align), c.hideOnMobile && "max-md:hidden", c.className)}>
                    {content}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>,
      )}
      {props.hasMore && (
        <div className="border-t border-line-2 py-2 text-center">
          <button type="button" className="btn-ghost" disabled={props.loadingMore} onClick={props.onLoadMore}>
            {props.loadingMore ? t("common.loading") : t("common.loadMore")}
          </button>
        </div>
      )}
    </>
  );
}
